import { isDefined } from "@clipboard-health/util-ts";
import { alpha, Box, CardContent, DialogContent, RadioGroup, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { RadioField } from "@src/appV2/lib/FormFields/RadioField/RadioField";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import {
  BottomSheet,
  Card,
  List,
  ListItem,
  Text,
  type UseModalState,
} from "@src/appV2/redesign/system/ui-components";
import { useCallback, useState } from "react";

import travelSuitcaseImage from "./assets/images/travelSuitcase.png";
import {
  TRAVEL_SHIFTS_BOTTOM_SHEET_GRADIENT,
  TRAVEL_SHIFTS_RESPONSES,
  type TravelShiftsResponse,
} from "./constants";

interface TravelShiftsBottomSheetProps {
  modalState: UseModalState;
  workerId: string;
  onSubmit: (response: TravelShiftsResponse) => void;
}

export function TravelShiftsBottomSheet(props: TravelShiftsBottomSheetProps) {
  const { modalState, workerId, onSubmit } = props;
  const [selectedResponse, setSelectedResponse] = useState<TravelShiftsResponse | "">("");

  useLogEffect(
    APP_V2_USER_EVENTS.TRAVEL_SHIFTS_BOTTOM_SHEET_VIEWED,
    { workerId },
    { enabled: modalState.modalIsOpen }
  );

  const handleConfirm = useCallback(() => {
    if (!selectedResponse) {
      return;
    }

    logEvent(APP_V2_USER_EVENTS.TRAVEL_SHIFTS_INTEREST_SUBMITTED, {
      workerId,
      response: selectedResponse,
    });

    onSubmit(selectedResponse);
    modalState.closeModal();
  }, [selectedResponse, workerId, onSubmit, modalState]);

  function handleDismiss(): void {
    logEvent(APP_V2_USER_EVENTS.TRAVEL_SHIFTS_BOTTOM_SHEET_DISMISSED, {
      workerId,
    });
  }

  function handleClose(): void {
    handleDismiss();
    modalState.closeModal();
  }

  const handleResponseChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedResponse(event.target.value as TravelShiftsResponse);
  }, []);

  return (
    <BottomSheet
      modalState={modalState}
      paperSx={{
        minHeight: "90%",
        background: TRAVEL_SHIFTS_BOTTOM_SHEET_GRADIENT,
        overflow: "hidden",
      }}
      header={
        <BottomSheetHeader
          onClose={() => {
            handleClose();
          }}
        />
      }
      footer={
        <DialogFooter orientation="vertical">
          <Button
            fullWidth
            variant="contained"
            size="large"
            disabled={!selectedResponse}
            onClick={handleConfirm}
          >
            Confirm Selection
          </Button>
        </DialogFooter>
      }
      onClose={() => {
        handleDismiss();
      }}
    >
      <Box
        component="img"
        src={travelSuitcaseImage}
        alt=""
        sx={{
          position: "absolute",
          bottom: 0,
          right: 0,
          width: "70%",
          maxWidth: 320,
          aspectRatio: "1 / 1",
          objectFit: "contain",
          pointerEvents: "none",
          transform: "translate(20%, -25%)",
          opacity: 0.75,
        }}
      />
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", flex: 1, position: "relative" }}
      >
        <Stack gap={6}>
          <Text
            semibold
            variant="h2"
            sx={(theme) => ({ textAlign: "center", color: theme.palette.text.primary })}
          >
            Access shifts that require travel
          </Text>

          <Text
            variant="body1"
            sx={(theme) => ({ textAlign: "center", color: theme.palette.text.secondary })}
          >
            <Text bold inline sx={(theme) => ({ color: theme.palette.text.primary })}>
              Coming soon,
            </Text>{" "}
            qualified professionals will get access to bundles of shifts outside your home area,
            allowing you to commit to traveling for a series of shifts.
          </Text>
        </Stack>

        <Stack sx={{ marginTop: 8 }} gap={5}>
          <Text semibold variant="h5" sx={{ textAlign: "center" }}>
            Examples of travel bundles outside your area
          </Text>
          <List dense disablePadding sx={{ listStyleType: "disc", paddingLeft: 5 }}>
            {[
              "2 weeks of shifts near Helena, Montana",
              "5 days of consecutive shifts in Bellingham, WA",
              "8 week travel block in Lewiston, Idaho",
              "3 shifts at rural towns in Washington",
              "20+ open shifts weekly in Madison, Wisconsin",
              "And lots more!",
            ].map((example) => (
              <ListItem
                key={example}
                disableGutters
                disablePadding
                sx={{ display: "list-item", padding: 0, minHeight: 0 }}
              >
                <Text variant="body1">{example}</Text>
              </ListItem>
            ))}
          </List>
          <Text variant="body2" sx={{ color: (theme) => theme.palette.text.secondary }}>
            Note: travel stipends and reimbursements not guaranteed and dependent on the specific
            facility
          </Text>
        </Stack>

        <Stack sx={{ flex: 1, justifyContent: "center", marginY: 8 }}>
          <Card
            variant="tertiary"
            sx={(theme) => ({
              ...(isDefined(theme.palette.background.tertiary) && {
                backgroundColor: alpha(theme.palette.background.tertiary, 0.9),
              }),
            })}
          >
            <CardContent>
              <Stack spacing={3}>
                <Text semibold variant="h6">
                  Interested in travel shifts like these?
                </Text>
                <Text variant="body2" sx={{ color: (theme) => theme.palette.text.secondary }}>
                  If so, we&apos;ll notify you when it&apos;s available.
                </Text>
                <RadioGroup value={selectedResponse} onChange={handleResponseChange}>
                  <RadioField
                    value={TRAVEL_SHIFTS_RESPONSES.INTERESTED}
                    label={
                      <Text sx={(theme) => ({ color: theme.palette.intent?.success?.text })}>
                        Yes! I am interested!
                      </Text>
                    }
                  />
                  <RadioField
                    value={TRAVEL_SHIFTS_RESPONSES.NOT_INTERESTED}
                    label={
                      <Text sx={(theme) => ({ color: theme.palette.intent?.error?.text })}>
                        No. I am not interested
                      </Text>
                    }
                  />
                </RadioGroup>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
